import type { WorkspaceChangedScopeV1 } from "@amplecare/contracts";

export interface WorkspaceRefreshScopeStatus {
  key: string;
  aliases: readonly string[];
  activeRegistrations: number;
  errorMessage: string | null;
  generatedAt: string | null;
  isRefreshing: boolean;
  lastAttemptAt: number | null;
  lastErrorAt: number | null;
  lastSuccessAt: number | null;
  nextRetryAt: number | null;
  revision: string | null;
}

export interface RegisterWorkspaceRefreshScopeInput<TPayload> {
  key: string;
  aliases?: readonly string[];
  loader: (signal: AbortSignal) => Promise<TPayload>;
  apply: (payload: TPayload) => void;
  onError?: (error: unknown) => void;
  readGeneratedAt?: (payload: TPayload) => string | null;
  readRevision?: (payload: TPayload) => string | null;
  minStaleMs: number;
  activePollMs?: number | null;
  debounceMs?: number;
  refreshOnRegister?: boolean;
}

export interface WorkspaceRefreshInvalidateOptions {
  broadcast?: boolean;
  local?: boolean;
  tenantId?: string | null;
}

interface WorkspaceRefreshBroadcastMessage {
  schemaVersion: "workspace-refresh-broadcast-v1";
  occurredAt: string;
  reason: string;
  scopes: string[];
  sourceTabId: string;
  tenantId: string | null;
}

interface WorkspaceRefreshScopeEntry {
  key: string;
  aliases: string[];
  loader: (signal: AbortSignal) => Promise<unknown>;
  apply: (payload: unknown) => void;
  onError: ((error: unknown) => void) | null;
  readGeneratedAt: ((payload: unknown) => string | null) | null;
  readRevision: ((payload: unknown) => string | null) | null;
  minStaleMs: number;
  activePollMs: number | null;
  debounceMs: number;
  activeRegistrations: number;
  failureCount: number;
  abortController: AbortController | null;
  debounceTimer: ReturnType<typeof setTimeout> | null;
  inFlight: Promise<void> | null;
  invalidationGeneration: number;
  pendingRefreshReason: string | null;
  pollTimer: ReturnType<typeof setTimeout> | null;
  status: WorkspaceRefreshScopeStatus;
}

const broadcastChannelName = "amplecare-workspace-refresh-v1";
const broadcastStorageKey = "amplecare.workspace-refresh.broadcast-v1";
const defaultDebounceMs = 220;
export const workspaceRefreshCadenceMs = {
  hidden: 300_000,
  liveCall: 5_000,
  staticRecovery: 300_000,
  standard: 60_000,
} as const;
const failureBackoffMs = [5_000, 15_000, 30_000, 60_000] as const;

const entries = new Map<string, WorkspaceRefreshScopeEntry>();
const emptyStatuses = new Map<string, WorkspaceRefreshScopeStatus>();
const listeners = new Set<() => void>();
const sourceTabId = createSourceTabId();

let browserBridgeInitialized = false;
let broadcastChannel: BroadcastChannel | null = null;

function createSourceTabId(): string {
  const cryptoLike = globalThis.crypto;

  if (typeof cryptoLike?.randomUUID === "function") {
    return cryptoLike.randomUUID();
  }

  return `tab-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

function normalizeScopeKeys(
  keys: readonly string[] | undefined,
): string[] {
  return [
    ...new Set(
      (keys ?? [])
        .map((key) => key.trim())
        .filter((key) => key.length > 0),
    ),
  ];
}

function createStatus(
  key: string,
  aliases: readonly string[] = [],
): WorkspaceRefreshScopeStatus {
  return {
    key,
    aliases,
    activeRegistrations: 0,
    errorMessage: null,
    generatedAt: null,
    isRefreshing: false,
    lastAttemptAt: null,
    lastErrorAt: null,
    lastSuccessAt: null,
    nextRetryAt: null,
    revision: null,
  };
}

function getEmptyStatus(key: string): WorkspaceRefreshScopeStatus {
  const existing = emptyStatuses.get(key);

  if (existing) {
    return existing;
  }

  const nextStatus = createStatus(key);
  emptyStatuses.set(key, nextStatus);

  return nextStatus;
}

function emitStatusChange(): void {
  for (const listener of listeners) {
    listener();
  }
}

function updateEntryStatus(
  entry: WorkspaceRefreshScopeEntry,
  patch: Partial<WorkspaceRefreshScopeStatus>,
): void {
  entry.status = {
    ...entry.status,
    ...patch,
    activeRegistrations: entry.activeRegistrations,
    aliases: entry.aliases,
    key: entry.key,
  };
  emitStatusChange();
}

function readErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "workspace-refresh-failed";
}

function readObjectStringProperty(
  value: unknown,
  propertyName: string,
): string | null {
  if (typeof value !== "object" || value == null) {
    return null;
  }

  const propertyValue = (value as Record<string, unknown>)[propertyName];

  return typeof propertyValue === "string" && propertyValue.length > 0
    ? propertyValue
    : null;
}

function readGeneratedAt(payload: unknown): string | null {
  return readObjectStringProperty(payload, "generatedAt");
}

function readRevision(payload: unknown): string | null {
  const projectionVersion = readObjectStringProperty(payload, "projectionVersion");
  const generatedAt = readGeneratedAt(payload);

  if (projectionVersion && generatedAt) {
    return `${projectionVersion}:${generatedAt}`;
  }

  return projectionVersion ?? generatedAt;
}

function readEntryGeneratedAt(
  entry: WorkspaceRefreshScopeEntry,
  payload: unknown,
): string | null {
  return entry.readGeneratedAt?.(payload) ?? readGeneratedAt(payload);
}

function readEntryRevision(
  entry: WorkspaceRefreshScopeEntry,
  payload: unknown,
): string | null {
  return entry.readRevision?.(payload) ?? readRevision(payload);
}

function resolveBackoffMs(failureCount: number): number {
  return (
    failureBackoffMs[
      Math.min(Math.max(failureCount - 1, 0), failureBackoffMs.length - 1)
    ] ?? failureBackoffMs[failureBackoffMs.length - 1]
  );
}

function isAbortError(error: unknown): boolean {
  return (
    (typeof DOMException !== "undefined" &&
      error instanceof DOMException &&
      error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function isEntryStale(entry: WorkspaceRefreshScopeEntry, now = Date.now()): boolean {
  return (
    entry.status.lastSuccessAt == null ||
    now - entry.status.lastSuccessAt >= entry.minStaleMs
  );
}

function clearTimer(timer: ReturnType<typeof setTimeout> | null): void {
  if (timer) {
    clearTimeout(timer);
  }
}

function schedulePoll(entry: WorkspaceRefreshScopeEntry): void {
  clearTimer(entry.pollTimer);
  entry.pollTimer = null;

  if (entry.activeRegistrations <= 0 || entry.activePollMs == null) {
    return;
  }

  const pollDelay = isDocumentHidden()
    ? Math.max(entry.activePollMs, workspaceRefreshCadenceMs.hidden)
    : entry.activePollMs;
  const retryDelay =
    entry.status.nextRetryAt == null
      ? 0
      : Math.max(entry.status.nextRetryAt - Date.now(), 0);

  entry.pollTimer = setTimeout(() => {
    entry.pollTimer = null;
    void refreshEntry(entry, "poll");
  }, Math.max(pollDelay, retryDelay));
}

function scheduleEntryRefresh(
  entry: WorkspaceRefreshScopeEntry,
  reason: string,
  delayMs = entry.debounceMs,
  force = false,
): void {
  if (entry.activeRegistrations <= 0) {
    return;
  }

  clearTimer(entry.debounceTimer);
  clearTimer(entry.pollTimer);
  entry.pollTimer = null;

  const retryDelay =
    force || entry.status.nextRetryAt == null
      ? 0
      : Math.max(entry.status.nextRetryAt - Date.now(), 0);

  entry.debounceTimer = setTimeout(() => {
    entry.debounceTimer = null;
    void refreshEntry(entry, reason, force);
  }, Math.max(delayMs, retryDelay));
}

async function refreshEntry(
  entry: WorkspaceRefreshScopeEntry,
  reason: string,
  force = false,
): Promise<void> {
  if (entry.activeRegistrations <= 0) {
    return;
  }

  if (entry.inFlight) {
    // A mutation or cross-tab invalidation can arrive while an older read is
    // still running. Returning only that older promise would lose the newer
    // revision until the next poll. Coalesce any such signals into one forced
    // follow-up read after the current request settles.
    if (reason !== "poll" || force) {
      entry.pendingRefreshReason = reason;
    }

    return entry.inFlight;
  }

  const now = Date.now();
  const startedInvalidationGeneration = entry.invalidationGeneration;

  if (!force && entry.status.nextRetryAt != null && entry.status.nextRetryAt > now) {
    schedulePoll(entry);
    return;
  }

  clearTimer(entry.pollTimer);
  entry.pollTimer = null;

  const abortController = new AbortController();
  entry.abortController = abortController;
  updateEntryStatus(entry, {
    errorMessage: null,
    isRefreshing: true,
    lastAttemptAt: now,
  });

  entry.inFlight = entry
    .loader(abortController.signal)
    .then((payload) => {
      if (
        abortController.signal.aborted ||
        entry.activeRegistrations <= 0 ||
        startedInvalidationGeneration !== entry.invalidationGeneration
      ) {
        return;
      }

      entry.apply(payload);

      updateEntryStatus(entry, {
        generatedAt: readEntryGeneratedAt(entry, payload),
        isRefreshing: false,
        lastSuccessAt: Date.now(),
        nextRetryAt: null,
        revision: readEntryRevision(entry, payload),
      });
      entry.failureCount = 0;
    })
    .catch((error: unknown) => {
      if (abortController.signal.aborted || isAbortError(error)) {
        updateEntryStatus(entry, {
          isRefreshing: false,
        });
        return;
      }

      // The follow-up read owns error reporting once an invalidation has
      // superseded this request. An obsolete failure must not flash an error,
      // invoke session handlers, or impose retry backoff on that newer read.
      if (startedInvalidationGeneration !== entry.invalidationGeneration) {
        return;
      }

      entry.failureCount += 1;
      const nextRetryAt = Date.now() + resolveBackoffMs(entry.failureCount);

      updateEntryStatus(entry, {
        errorMessage: readErrorMessage(error),
        isRefreshing: false,
        lastErrorAt: Date.now(),
        nextRetryAt,
      });
      entry.onError?.(error);
    })
    .finally(() => {
      if (entry.abortController === abortController) {
        entry.abortController = null;
      }

      const pendingRefreshReason = entry.pendingRefreshReason;
      entry.pendingRefreshReason = null;
      entry.inFlight = null;

      if (pendingRefreshReason && entry.activeRegistrations > 0) {
        clearTimer(entry.debounceTimer);
        entry.debounceTimer = null;
        void refreshEntry(
          entry,
          pendingRefreshReason,
          // Invalidations represent a revision newer than the request that
          // just completed, so do not let a prior failure backoff suppress it.
          true,
        );
        return;
      }

      schedulePoll(entry);
    });

  return entry.inFlight;
}

function findMatchingEntries(scopeKey: string): WorkspaceRefreshScopeEntry[] {
  const matches: WorkspaceRefreshScopeEntry[] = [];

  for (const entry of entries.values()) {
    if (entry.key === scopeKey || entry.aliases.includes(scopeKey)) {
      matches.push(entry);
    }
  }

  return matches;
}

function resolveTenantIdFromScopeKeys(keys: readonly string[]): string | null {
  for (const key of keys) {
    const [, tenantId] = key.split(":");

    if (tenantId) {
      return tenantId;
    }
  }

  return null;
}

function isBroadcastMessage(value: unknown): value is WorkspaceRefreshBroadcastMessage {
  if (typeof value !== "object" || value == null) {
    return false;
  }

  const candidate = value as WorkspaceRefreshBroadcastMessage;

  return (
    candidate.schemaVersion === "workspace-refresh-broadcast-v1" &&
    typeof candidate.sourceTabId === "string" &&
    Array.isArray(candidate.scopes) &&
    candidate.scopes.every((scope) => typeof scope === "string") &&
    typeof candidate.reason === "string"
  );
}

function handleBroadcastMessage(message: unknown): void {
  if (!isBroadcastMessage(message) || message.sourceTabId === sourceTabId) {
    return;
  }

  invalidateWorkspaceRefreshScopes(message.scopes, message.reason, {
    broadcast: false,
    tenantId: message.tenantId,
  });
}

function publishBroadcastMessage(
  scopes: readonly string[],
  reason: string,
  tenantId: string | null,
): void {
  if (scopes.length === 0 || typeof window === "undefined") {
    return;
  }

  ensureBrowserBridge();

  const message: WorkspaceRefreshBroadcastMessage = {
    schemaVersion: "workspace-refresh-broadcast-v1",
    occurredAt: new Date().toISOString(),
    reason,
    scopes: [...scopes],
    sourceTabId,
    tenantId,
  };

  try {
    broadcastChannel?.postMessage(message);
  } catch {}

  try {
    window.localStorage?.setItem(
      broadcastStorageKey,
      JSON.stringify(message),
    );
  } catch {}
}

function ensureBrowserBridge(): void {
  if (browserBridgeInitialized || typeof window === "undefined") {
    return;
  }

  browserBridgeInitialized = true;

  if (typeof BroadcastChannel !== "undefined") {
    try {
      broadcastChannel = new BroadcastChannel(broadcastChannelName);
      broadcastChannel.onmessage = (event) => {
        handleBroadcastMessage(event.data);
      };
    } catch {
      broadcastChannel = null;
    }
  }

  window.addEventListener("storage", (event) => {
    if (event.key !== broadcastStorageKey || !event.newValue) {
      return;
    }

    try {
      handleBroadcastMessage(JSON.parse(event.newValue) as unknown);
    } catch {}
  });

  const refreshStaleVisibleScopes = (reason: string) => {
    if (isDocumentHidden()) {
      return;
    }

    for (const entry of entries.values()) {
      if (entry.activeRegistrations > 0 && isEntryStale(entry)) {
        scheduleEntryRefresh(entry, reason, 0);
      } else {
        schedulePoll(entry);
      }
    }
  };

  window.addEventListener("focus", () => {
    refreshStaleVisibleScopes("window-focus");
  });

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        refreshStaleVisibleScopes("visibility-visible");
        return;
      }

      for (const entry of entries.values()) {
        schedulePoll(entry);
      }
    });
  }
}

export function subscribeWorkspaceRefreshScopes(
  listener: () => void,
): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getWorkspaceRefreshScopeStatus(
  key: string,
): WorkspaceRefreshScopeStatus {
  return entries.get(key)?.status ?? getEmptyStatus(key);
}

export function registerWorkspaceRefreshScope<TPayload>(
  input: RegisterWorkspaceRefreshScopeInput<TPayload>,
): () => void {
  ensureBrowserBridge();

  const key = input.key.trim();

  if (!key) {
    return () => {};
  }

  const aliases = normalizeScopeKeys(input.aliases);
  const existing = entries.get(key);
  const entry: WorkspaceRefreshScopeEntry =
    existing ??
    {
      key,
      aliases,
      loader: input.loader as (signal: AbortSignal) => Promise<unknown>,
      apply: input.apply as (payload: unknown) => void,
      onError: (input.onError as ((error: unknown) => void) | undefined) ?? null,
      readGeneratedAt:
        (input.readGeneratedAt as
          | ((payload: unknown) => string | null)
          | undefined) ?? null,
      readRevision:
        (input.readRevision as
          | ((payload: unknown) => string | null)
          | undefined) ?? null,
      minStaleMs: input.minStaleMs,
      activePollMs: input.activePollMs ?? null,
      debounceMs: input.debounceMs ?? defaultDebounceMs,
      activeRegistrations: 0,
      failureCount: 0,
      abortController: null,
      debounceTimer: null,
      inFlight: null,
      invalidationGeneration: 0,
      pendingRefreshReason: null,
      pollTimer: null,
      status: createStatus(key, aliases),
    };

  entry.aliases = aliases;
  entry.loader = input.loader as (signal: AbortSignal) => Promise<unknown>;
  entry.apply = input.apply as (payload: unknown) => void;
  entry.onError = (input.onError as ((error: unknown) => void) | undefined) ?? null;
  entry.readGeneratedAt =
    (input.readGeneratedAt as
      | ((payload: unknown) => string | null)
      | undefined) ?? null;
  entry.readRevision =
    (input.readRevision as
      | ((payload: unknown) => string | null)
      | undefined) ?? null;
  entry.minStaleMs = input.minStaleMs;
  entry.activePollMs = input.activePollMs ?? null;
  entry.debounceMs = input.debounceMs ?? defaultDebounceMs;
  entry.activeRegistrations += 1;

  entries.set(key, entry);
  updateEntryStatus(entry, {});

  if (input.refreshOnRegister) {
    scheduleEntryRefresh(entry, "register", 0);
  } else {
    schedulePoll(entry);
  }

  return () => {
    const currentEntry = entries.get(key);

    if (!currentEntry) {
      return;
    }

    currentEntry.activeRegistrations = Math.max(
      currentEntry.activeRegistrations - 1,
      0,
    );

    if (currentEntry.activeRegistrations > 0) {
      updateEntryStatus(currentEntry, {});
      return;
    }

    currentEntry.abortController?.abort();
    clearTimer(currentEntry.debounceTimer);
    clearTimer(currentEntry.pollTimer);
    currentEntry.pendingRefreshReason = null;
    entries.delete(key);
    emitStatusChange();
  };
}

export function invalidateWorkspaceRefreshScope(
  key: string,
  reason: string,
  options: WorkspaceRefreshInvalidateOptions = {},
): void {
  invalidateWorkspaceRefreshScopes([key], reason, options);
}

export function invalidateWorkspaceRefreshScopes(
  keys: readonly string[],
  reason: string,
  options: WorkspaceRefreshInvalidateOptions = {},
): void {
  const scopeKeys = normalizeScopeKeys(keys);
  const matchingEntries = [
    ...new Set(scopeKeys.flatMap((scopeKey) => findMatchingEntries(scopeKey))),
  ];

  if (options.local !== false) {
    for (const entry of matchingEntries) {
      entry.invalidationGeneration += 1;

      if (entry.inFlight) {
        entry.pendingRefreshReason = reason;
      }

      scheduleEntryRefresh(entry, reason, entry.debounceMs, true);
    }
  }

  if (options.broadcast !== false) {
    publishBroadcastMessage(
      scopeKeys,
      reason,
      options.tenantId ?? resolveTenantIdFromScopeKeys(scopeKeys),
    );
  }
}

export async function refreshWorkspaceRefreshScopeNow(
  key: string,
  reason: string,
): Promise<void> {
  await Promise.all(
    findMatchingEntries(key).map((entry) => {
      entry.invalidationGeneration += 1;
      return refreshEntry(entry, reason, true);
    }),
  );
}

export function hashWorkspaceRefreshKeyPayload(payload: unknown): string {
  const text =
    typeof payload === "string" ? payload : JSON.stringify(payload) ?? "";
  let hash = 0x811c9dc5;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(36);
}

export function buildTenantAdminSummaryRefreshKey(input: {
  analyticsKey: string;
  tenantId: string;
}): string {
  return `tenant-admin-summary:${input.tenantId}:${hashWorkspaceRefreshKeyPayload(
    input.analyticsKey,
  )}`;
}

export function buildTenantAdminSummaryRefreshAlias(tenantId: string): string {
  return `tenant-admin-summary:${tenantId}:all`;
}

export function buildTenantAdminPatientOperationsRefreshKey(input: {
  requestKey: string;
  tenantId: string;
}): string {
  return `tenant-admin-patient-operations:${input.tenantId}:${hashWorkspaceRefreshKeyPayload(
    input.requestKey,
  )}`;
}

export function buildTenantAdminPatientOperationsRefreshAlias(
  tenantId: string,
): string {
  return `tenant-admin-patient-operations:${tenantId}:all`;
}

export function buildTenantAdminClinicianOperationsRefreshKey(input: {
  requestKey: string;
  tenantId: string;
}): string {
  return `tenant-admin-clinician-operations:${input.tenantId}:${hashWorkspaceRefreshKeyPayload(
    input.requestKey,
  )}`;
}

export function buildTenantAdminClinicianOperationsRefreshAlias(
  tenantId: string,
): string {
  return `tenant-admin-clinician-operations:${tenantId}:all`;
}

export function buildTenantAdminReviewQueueRefreshKey(input: {
  queueId: string;
  requestKey: string;
  tenantId: string;
}): string {
  return `tenant-admin-review-queue:${input.tenantId}:${input.queueId}:${hashWorkspaceRefreshKeyPayload(
    input.requestKey,
  )}`;
}

export function buildTenantAdminReviewQueueRefreshAlias(tenantId: string): string {
  return `tenant-admin-review-queue:${tenantId}:all`;
}

export function buildTenantAdminOutboundRefreshKey(input: {
  requestKey: string;
  tenantId: string;
}): string {
  return `tenant-admin-outbound:${input.tenantId}:${hashWorkspaceRefreshKeyPayload(
    input.requestKey,
  )}`;
}

export function buildTenantAdminOutboundRefreshAlias(tenantId: string): string {
  return `tenant-admin-outbound:${tenantId}:all`;
}

export function buildClinicianWorkbenchRefreshKey(input: {
  routeKey: string;
  tenantId: string;
  clinicianUserId: string;
}): string {
  return `clinician-workbench:${input.tenantId}:${
    hashWorkspaceRefreshKeyPayload(input.clinicianUserId)
  }:${hashWorkspaceRefreshKeyPayload(input.routeKey)}`;
}

export function buildClinicianWorkbenchRefreshAliases(input: {
  tenantId: string;
  clinicianUserId: string;
}): string[] {
  return [
    buildClinicianWorkbenchRefreshTenantAlias(input.tenantId),
    `clinician-workbench:${input.tenantId}:${hashWorkspaceRefreshKeyPayload(
      input.clinicianUserId,
    )}:all`,
  ];
}

export function buildClinicianWorkbenchRefreshTenantAlias(tenantId: string): string {
  return `clinician-workbench:${tenantId}:all`;
}

export function buildClinicianWorkbenchOutboundCallsRefreshKey(input: {
  patientId: string;
  tenantId: string;
}): string {
  return `clinician-workbench-outbound:${input.tenantId}:${hashWorkspaceRefreshKeyPayload(
    input.patientId,
  )}`;
}

export function buildClinicianWorkbenchOutboundCallsRefreshAliases(input: {
  tenantId: string;
  clinicianUserId: string;
}): string[] {
  return [
    buildClinicianWorkbenchRefreshTenantAlias(input.tenantId),
    ...buildClinicianWorkbenchRefreshAliases(input),
    `clinician-workbench-outbound:${input.tenantId}:all`,
  ];
}

export function buildPatientWorkspaceRefreshKey(input: {
  patientRef: string;
  tenantId: string;
}): string {
  return `patient-workspace:${input.tenantId}:${hashWorkspaceRefreshKeyPayload(
    input.patientRef,
  )}`;
}

export function buildPatientWorkspaceRefreshAlias(tenantId: string): string {
  return `patient-workspace:${tenantId}:all`;
}

export function buildWorkspaceRefreshAliasesForChangedScopes(
  changedScopes: readonly WorkspaceChangedScopeV1[] | null | undefined,
): string[] {
  if (!changedScopes || changedScopes.length === 0) {
    return [];
  }

  const aliases = changedScopes.flatMap((scope): string[] => {
    switch (scope.kind) {
      case "tenant-admin-summary":
        return [buildTenantAdminSummaryRefreshAlias(scope.tenantId)];
      case "tenant-admin-patient-operations":
        return [buildTenantAdminPatientOperationsRefreshAlias(scope.tenantId)];
      case "tenant-admin-clinician-operations":
        return [buildTenantAdminClinicianOperationsRefreshAlias(scope.tenantId)];
      case "tenant-admin-review-queue":
        return [buildTenantAdminReviewQueueRefreshAlias(scope.tenantId)];
      case "tenant-admin-outbound":
        return [buildTenantAdminOutboundRefreshAlias(scope.tenantId)];
      case "clinician-workbench":
        return scope.clinicianUserId
          ? buildClinicianWorkbenchRefreshAliases({
              clinicianUserId: scope.clinicianUserId,
              tenantId: scope.tenantId,
            })
          : [buildClinicianWorkbenchRefreshTenantAlias(scope.tenantId)];
      case "patient-workspace":
        return [buildPatientWorkspaceRefreshAlias(scope.tenantId)];
    }
  });

  return normalizeScopeKeys(aliases);
}

export function __resetWorkspaceRefreshServiceForTests(): void {
  for (const entry of entries.values()) {
    entry.abortController?.abort();
    clearTimer(entry.debounceTimer);
    clearTimer(entry.pollTimer);
  }

  entries.clear();
  emptyStatuses.clear();
  listeners.clear();
}
